import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {popupCloseSubscriptions} from '@components/common/popup/popup'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {getTemplate} from '@components/movement-templates/helpers/get-template'
import {TextArea} from '@components/textarea'
import {noPropagate} from '@containers/hooks'
import {useAddTaskStore} from './add-task-store'
import {creatorProcessKeyDown} from './creator-process-keydown'

const TwTextEditContainer = tw.div<{$small?: boolean}>`
  flex
  flex-col
  px-20
  max-h-200
  ${p => (p.$small ? 'min-h-16' : 'min-h-64')}
`

const TwTextArea = tw(TextArea)`
  !border-none
  !outline-none
  !shadow-none
  !bg-transparent
  !overflow-hidden
  !w-full
  p-0
  m-0
  leading-4
  text-14
  text-primary
  placeholder:text-placeholder
`

const TwTextareaScrollContainer = tw(WithPerfectScrollbar)`
  flex-1
  pr-8
  -mr-8
`

export const TextEdit = observer(
  ({small, toEnd}: {small?: boolean; toEnd?: boolean}) => {
    const [state, store] = useAddTaskStore()
    const refTextarea = useRef({} as HTMLTextAreaElement)

    const focus = () => {
      setTimeout(() => refTextarea.current?.focus(), 0)
    }
    useEffect(() => {
      focus()
      store.reset({
        needClose: false,
        resetStickers: 'all'
      })

      popupCloseSubscriptions['add-task'] = () => {
        store.reset({
          needClose: true,
          resetStickers: 'all',
          resetTemplateId: true
        })
      }
    }, [])

    useEffect(() => {
      const listener = (ev: KeyboardEvent) => {
        const eventKey = ev.key.toLocaleLowerCase()
        if (eventKey === 'enter') {
          ev.stopPropagation()
          onEnter()
          focus()
        }
      }
      window.addEventListener('keydown', listener)
      return () => {
        window.removeEventListener('keydown', listener)
      }
    })

    useEffect(() => {
      if (!state.isTextEditShowed) {
        store.updateText('')
        return
      }
      if (!state.templateId) {
        return
      }

      const template = getTemplate(state.templateId)
      const newText = template.data.taskTemplate.title
      store.updateText(newText)

      setTimeout(() => {
        refTextarea.current.setSelectionRange(0, newText.length)
      }, 100)
    }, [state.templateId, state.isTextEditShowed])

    const onEnter = () => {
      store.createTask({resetStickers: 'required', toEnd})
    }
    const onCancel = () => {
      store.reset({
        needClose: true,
        resetStickers: 'all',
        resetTemplateId: true
      })
      return true
    }
    const onBlur = () => {
      // store.onTextEditBlur(toEnd)
    }
    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target?.value || ''
      store.updateText(text)
    }

    const onKeyDown = creatorProcessKeyDown({onCancel, onEnter, refTextarea})

    return (
      <TwTextEditContainer $small={small}>
        <TwTextareaScrollContainer>
          <TwTextArea
            data-test="board-task-input-name"
            ref={refTextarea}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            placeholder={Lang.msg.taskPlaceholder}
            value={state.text}
            onChange={onChange}
            onMouseDown={noPropagate}
          />
        </TwTextareaScrollContainer>
      </TwTextEditContainer>
    )
  }
)
